import React, { useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import './App.css';
import './location.css';
import Header from './components/Header';
import Hero from './components/Hero';
import EventSetup from './components/EventSetup';
import BudgetSection from './components/BudgetSection';
import Adjustments from './components/Adjustments';
import BudgetSummary from './components/BudgetSummary';
import MetricCard from './components/MetricCard';
import PerformanceModel from './components/PerformanceModel';
import Insights from './components/Insights';
import { initialBudgetItems, presets } from './data/budgetItems';
import { defaultLocation } from './data/eventLocations';
import {
  calculateBudget,
  formatCurrency,
  formatNumber
} from './utils/calculations';
import { downloadBudgetReport } from './utils/report';

const { FiUsers, FiTarget, FiDollarSign, FiLayers } = FiIcons;

const initialSettings = {
  eventName: '2025 Annual Scientific Meeting',
  attendees: 2800,
  days: 4,
  budgetCap: 100000,
  discountRate: 8,
  contingencyRate: 7,
  taxRate: 2.5,
  projectedLeads: 420,
  conversionRate: 12,
  valuePerConversion: 3500,
  ...defaultLocation
};

const getSavedPlan = () => {
  try {
    const saved = localStorage.getItem('medical-conference-budget');
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

function App() {
  const savedPlan = getSavedPlan();
  const [items, setItems] = useState(savedPlan?.items || initialBudgetItems);
  const [settings, setSettings] = useState({
    ...initialSettings,
    ...(savedPlan?.settings || {})
  });
  const [lastSaved, setLastSaved] = useState('just now');

  const calculations = useMemo(
    () => calculateBudget(items, settings),
    [items, settings]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      localStorage.setItem(
        'medical-conference-budget',
        JSON.stringify({ items, settings })
      );
      setLastSaved(
        new Intl.DateTimeFormat('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        }).format(new Date())
      );
    }, 350);

    return () => window.clearTimeout(timer);
  }, [items, settings]);

  const updateSetting = (field, value) => {
    setSettings((current) => ({ ...current, [field]: value }));
  };

  const updateItem = (id, field, value) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const applyPreset = (name) => {
    const preset = presets[name];
    setSettings((current) => ({ ...current, budgetCap: preset.budget }));
    setItems((current) =>
      current.map((item) => ({
        ...item,
        enabled: preset.enabledIds.includes(item.id)
      }))
    );
  };

  const resetPlan = () => {
    setItems(initialBudgetItems);
    setSettings(initialSettings);
    localStorage.removeItem('medical-conference-budget');
  };

  const categories = [...new Set(items.map((item) => item.category))];

  return (
    <div className="app-shell">
      <Header
        onReset={resetPlan}
        onDownloadReport={() => downloadBudgetReport({ settings, calculations })}
      />

      <main>
        <Hero lastSaved={lastSaved} total={calculations.total} />
        <EventSetup
          settings={settings}
          onChange={updateSetting}
          onPreset={applyPreset}
        />

        <section className="metrics-grid">
          <MetricCard
            icon={FiLayers}
            label="Selected placements"
            value={formatNumber(calculations.activeItems.length)}
            detail="Across all media categories"
            tone="blue"
          />
          <MetricCard
            icon={FiUsers}
            label="Cost per attendee"
            value={formatCurrency(calculations.costPerAttendee, 2)}
            detail={`${formatNumber(settings.attendees)} expected attendees`}
            tone="teal"
          />
          <MetricCard
            icon={FiTarget}
            label="Cost per lead"
            value={formatCurrency(calculations.costPerLead, 2)}
            detail={`${formatNumber(settings.projectedLeads)} projected leads`}
            tone="purple"
          />
          <MetricCard
            icon={FiDollarSign}
            label="Projected value"
            value={formatCurrency(calculations.projectedValue)}
            detail={`${calculations.roi.toFixed(1)}% estimated ROI`}
            tone="orange"
          />
        </section>

        <div className="workspace">
          <div className="workspace-main">
            <section className="inventory-heading">
              <div>
                <span className="section-kicker">Advertising inventory</span>
                <h2>Select placements and customize rates</h2>
              </div>
              <p>All rates are editable planning estimates in USD.</p>
            </section>

            <div className="budget-sections">
              {categories.map((category) => (
                <BudgetSection
                  key={category}
                  category={category}
                  items={items.filter((item) => item.category === category)}
                  onUpdate={updateItem}
                />
              ))}
            </div>

            <Adjustments settings={settings} onChange={updateSetting} />
            <PerformanceModel
              settings={settings}
              calculations={calculations}
              onChange={updateSetting}
            />
            <Insights calculations={calculations} settings={settings} />
          </div>

          <div className="workspace-side">
            <BudgetSummary
              calculations={calculations}
              budgetCap={settings.budgetCap}
              settings={settings}
            />
          </div>
        </div>

        <footer>
          <strong>{settings.eventName}</strong>
          <span>
            Planning estimates only · Validate rates, taxes, compliance, and
            contractual terms with the medical society.
          </span>
        </footer>
      </main>
    </div>
  );
}

export default App;